import type { IndicatorConfig } from '@/types/indicators';
import type { IndicatorData } from '@/types/dashboard';
import { formatterRegistry } from '@/utils/formatters';
import { DeltaBadge } from '@/components/DeltaBadge';

function withAlpha20(color: string): string {
  const hex = color.startsWith('#') ? color : `#${color}`;
  return `${hex}20`;
}

export function IndicatorWidget({ config, data }: { config: IndicatorConfig; data: IndicatorData | undefined }) {
  const isText = typeof data?.value === 'string' && isNaN(Number(data.value));
  const formattedValue = isText
    ? String(data.value)
    : (data?.value !== undefined && data?.value !== null
        ? formatterRegistry.format('number', data.value as number, {
            useMillions: config.useMillions,
            millionsFrom: config.millionsFrom,
            decimalPlaces: config.decimalPlaces,
          })
        : '—');

  const iconClass = config.icon?.trim();
  const iconBgColor = config.bgColor || config.color || '#3a57e8';
  const iconFgColor = config.color || iconBgColor;

  return (
    <div data-testid="indicator-widget">
      <div className="d-flex justify-content-start align-items-start h-100">
        {iconClass && (
          <div className="flex-shrink-0">
            <div
              className="p-3 rounded"
              style={{ backgroundColor: withAlpha20(iconBgColor), color: iconFgColor }}
            >
              <i className={`${iconClass} fs-2`} />
            </div>
          </div>
        )}

        <div className="flex-grow-1 mx-3">
          <div className="indicator-value">
            <h3 className="mb-0">
              {formattedValue === '—' ? '—' : <>{config.prefix ?? ''}{formattedValue}{config.suffix ?? ''}</>}
            </h3>
          </div>
          {config.label && (
            <p className="mb-0 text-uppercase small text-secondary">{config.label}</p>
          )}
        </div>

        {(data?.delta !== undefined && data?.delta !== null) && (
          <div className="flex-shrink-0">
            <DeltaBadge
              value={data.delta}
              prefix={config.deltaPrefix}
              suffix={config.deltaSuffix}
              colorUp={config.deltaColorUp}
              colorDown={config.deltaColorDown}
              colorNeutral={config.deltaColorNeutral}
              useMillions={config.useMillionsDelta}
              millionsFrom={config.millionsFromDelta}
              decimalPlaces={config.decimalPlacesDelta}
            />
          </div>
        )}
      </div>

      {config.description && (
        <div className="mt-2 small text-secondary w-100">{config.description}</div>
      )}
    </div>
  );
}
